import { atom, onConnect } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { pairPriceUpdateEvent } from '@root/entities/realtime'
import { newTxEvent } from '@root/entities/realtime/atoms/new-tx-event'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { reatomFactoryCache } from '@root/shared/lib/reatom'

import type { Network } from '@root/entities/network/model'
import type { PairAddress } from '@root/entities/pair/model'
import type { Price } from '@root/entities/pair/model/pair'
import type { PairPriceUpdateData } from '@root/entities/realtime'
import type { SubscriptionStrategy } from '@root/shared/lib/reatom/reatom-resource-registry'

type TxSocketPrice = Price & { timestamp: Date }

const reatomPairPriceChartSocketInternal = (options: {
  pairAddress: PairAddress
  network: Network.Network
}) => {
  const priceAtom = atom<TxSocketPrice | undefined>(undefined, 'priceAtom')

  onConnect(priceAtom, () => {
    // Listening to new txs without explicit subscription
    const unsubscribe = newTxEvent.onCall((ctx, tx) => {
      if (
        tx.pairAddress !== options.pairAddress ||
        tx.network !== options.network
      ) {
        return
      }
      priceAtom(ctx, {
        priceNative: BigNumber(tx.tokenPriceInNative),
        priceUsd: BigNumber(tx.tokenPriceInUsd),
        timestamp: new Date(tx.timestamp),
      })
    })

    return () => unsubscribe()
  })

  return priceAtom
}

// eslint-disable-next-line @reatom/unit-naming-rule
export const reatomPairPriceChartSocket = reatomFactoryCache({
  factory: reatomPairPriceChartSocketInternal,
  keyFn: (options) => `${options.pairAddress}-${options.network}`,
})

type SubscriptionOptions = {
  /**
   * Whether should subscribe to pair-price websocket event in main ws
   * @default 'without-subscription'
   */
  mainWebsocketSubscribeStrategy?: SubscriptionStrategy
}

const reatomPairPriceSocketInternal = (
  options: {
    pairAddress: PairAddress
    network: Network.Network
  } & SubscriptionOptions,
) => {
  const priceAtom = atom<PairPriceUpdateData | undefined>(
    undefined,
    'priceAtom',
  )

  onConnect(priceAtom, (ctx) => {
    const unsubscribe = pairPriceUpdateEvent.onCall((ctx, priceUpdate) => {
      if (
        priceUpdate.pairAddress !== options.pairAddress ||
        priceUpdate.network !== options.network
      ) {
        return
      }
      const currentUpdateAt = ctx.get(priceAtom)?.updatedAt
      if (currentUpdateAt && priceUpdate.updatedAt < currentUpdateAt) {
        return
      }
      priceAtom(ctx, priceUpdate)
    })
    const mainWebsocket = ctx.get(dependenciesAtom).mainWebsocket
    if (options.mainWebsocketSubscribeStrategy === 'with-subscription') {
      mainWebsocket.pairPriceSubscribeAction(ctx, {
        pairAddresses: [options.pairAddress],
        network: options.network,
      })
    }

    return () => {
      unsubscribe()
      if (options.mainWebsocketSubscribeStrategy === 'with-subscription') {
        mainWebsocket.pairPriceUnsubscribeAction(ctx, {
          pairAddresses: [options.pairAddress],
          network: options.network,
        })
      }
    }
  })

  return priceAtom
}
// eslint-disable-next-line @reatom/unit-naming-rule
export const reatomPairPriceSocket = reatomFactoryCache({
  factory: reatomPairPriceSocketInternal,
  keyFn: (options) =>
    `${options.pairAddress}-${options.network}-${options.mainWebsocketSubscribeStrategy}`,
})

/**
 * Takes latest price from main websocket and chart websocket
 */
export const reatomPairPriceLatest = (
  options: {
    pairAddress: PairAddress
    network: Network.Network
  } & SubscriptionOptions,
) => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const mainWebsocketPriceAtom = reatomPairPriceSocket(options)
  // eslint-disable-next-line @reatom/unit-naming-rule
  const chartSocketPriceAtom = reatomPairPriceChartSocket(options)

  const priceAtom = atom((ctx) => {
    const mainWebsocketPrice = ctx.spy(mainWebsocketPriceAtom)
    const chartSocketPrice = ctx.spy(chartSocketPriceAtom)

    if (mainWebsocketPrice && chartSocketPrice) {
      return mainWebsocketPrice.updatedAt > chartSocketPrice.timestamp
        ? mainWebsocketPrice
        : chartSocketPrice
    }

    return mainWebsocketPrice ?? chartSocketPrice
  }, 'priceAtom')

  return priceAtom
}
